// src/Components/ViewInfo/ViewInfo.js

import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Portal from '@mui/material/Portal';
import Slider from 'react-slick';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';

// Firebase (THE FIX: Added to fetch live state)
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CropIcon from '@mui/icons-material/Crop';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  MenuGeneralInfoColor,
  MenuGeneralInfoGrey,
  MenuDocsColor,
  MenuDocsGrey,
  ClipsIcon,
  SoundWaveIcon,
} from '../../assets/icons/CustomIcons';
import { MenuResetView, Grid5x5Icon } from '../../assets/icons/CustomIconsAdd4';

import InfoTab from '../Drawer/InfoTab/InfoTab';
import DocsView from './DocsView';
import MediaSlide from '../Shared/MediaSlide';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const SIDEBAR_WIDTH = 60;

export default function ViewInfo({ selectedMarker, onDeselect }) {
  const navigate = useNavigate();

  // --- DB Live State (THE FIX) ---
  const [liveMarker, setLiveMarker] = React.useState(selectedMarker);

  React.useEffect(() => {
    setLiveMarker(selectedMarker);
    let isMounted = true;

    const fetchLiveMedia = async () => {
      if (!selectedMarker) return;
      const groupId = selectedMarker.markerGroupID || selectedMarker.id;
      if (!groupId) return;

      try {
        const docRef = doc(db, 'markerGroups', groupId);
        const snap = await getDoc(docRef);
        if (snap.exists() && isMounted) {
          const data = snap.data();
          setLiveMarker((prev) => ({
            ...prev,
            photos: data.photos || [],
            documents: data.documents || [],
          }));
        }
      } catch (err) {
        console.error('Error fetching live media:', err);
      }
    };

    fetchLiveMedia();
    return () => {
      isMounted = false;
    };
  }, [selectedMarker]);

  // --- Core Display State ---
  const [activePanel, setActivePanel] = React.useState('INFO');

  // --- Photo Slider & Description States ---
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isCoverMode, setIsCoverMode] = React.useState(true);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = React.useState(false);
  const sliderRef = React.useRef(null);

  React.useEffect(() => {
    setCurrentIndex(0);
    setIsDescriptionExpanded(false);
    if (sliderRef.current) sliderRef.current.slickGoTo(0, true);
  }, [liveMarker]);

  // --- Layout, Dragging & Sizing States ---
  const [containerPos, setContainerPos] = React.useState({ x: 10, y: 150 });
  const [containerSize, setContainerSize] = React.useState(() =>
    Math.min(800, window.innerHeight - 160),
  );

  const [isDragging, setIsDragging] = React.useState(false);
  const dragInfo = React.useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });

  const resizeInfo = React.useRef({
    isResizing: false,
    startX: 0,
    startY: 0,
    initialSize: 0,
  });

  // --- ESCAPE KEY LISTENER ---
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        if (document.querySelector('.MuiDialog-root')) return;
        e.preventDefault();
        e.stopPropagation();

        if (onDeselect) onDeselect();
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [onDeselect]);

  // --- DRAG HANDLERS ---
  const handlePointerDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      dragInfo.current.isDragging = true;
      setIsDragging(true);
      dragInfo.current.startX = e.clientX;
      dragInfo.current.startY = e.clientY;
      dragInfo.current.initialX = containerPos.x;
      dragInfo.current.initialY = containerPos.y;

      try {
        if (e.target.setPointerCapture) {
          e.target.setPointerCapture(e.pointerId);
        }
      } catch (err) {}

      return;
    }

    if (
      e.target.closest('button') ||
      e.target.closest('a') ||
      e.target.closest('.slick-arrow') ||
      e.target.closest('.slick-dots') ||
      e.target.closest('.resize-handle') ||
      e.target.closest('.MuiInputBase-root') ||
      e.target.closest('.MuiCheckbox-root')
    ) {
      return;
    }
  };

  const handlePointerMove = (e) => {
    if (!dragInfo.current.isDragging) return;
    setContainerPos({
      x: dragInfo.current.initialX + (e.clientX - dragInfo.current.startX),
      y: dragInfo.current.initialY + (e.clientY - dragInfo.current.startY),
    });
  };

  const handlePointerUp = (e) => {
    if (!dragInfo.current.isDragging) return;
    dragInfo.current.isDragging = false;
    setIsDragging(false);

    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  // --- RESIZE HANDLERS ---
  const handleResizePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = true;
    resizeInfo.current.startX = e.clientX;
    resizeInfo.current.startY = e.clientY;
    resizeInfo.current.initialSize = containerSize;
    try {
      if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    } catch (err) {}
  };

  const handleResizePointerMove = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();

    const deltaX = e.clientX - resizeInfo.current.startX;
    const deltaY = e.clientY - resizeInfo.current.startY;

    const maxDelta = Math.max(deltaX, deltaY);
    const newSize = Math.max(400, resizeInfo.current.initialSize + maxDelta);

    setContainerSize(newSize);
  };

  const handleResizePointerUp = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = false;
    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const handleSidebarClick = (panel) => {
    setActivePanel(panel);
    setIsDescriptionExpanded(false);
  };

  const handleOpenWeb = (url) => {
    if (!url || url.trim() === '') return;
    let finalUrl = url.trim();
    if (!/^https?:\/\//i.test(finalUrl)) finalUrl = 'http://' + finalUrl;
    try {
      const parsedUrl = new URL(finalUrl);
      if (!parsedUrl.hostname.includes('.') && parsedUrl.hostname !== 'localhost')
        throw new Error();
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {}
  };

  // --- PHOTO SLIDER DATA PREP ---
  let activeSlides = [];
  if (liveMarker?.photos && liveMarker.photos.length > 0) {
    activeSlides = liveMarker.photos.map((p) => {
      let resolvedUrl = p.url || p.img || '';
      if (resolvedUrl === 'null' || resolvedUrl === 'undefined') resolvedUrl = '';
      let resolvedType = p.type || 'image';

      if (resolvedType === 'image') {
        const searchString = String(resolvedUrl + (p.fileName || '')).toLowerCase();
        if (searchString.match(/\.(mp4|mov|webm)/i)) resolvedType = 'video';
        if (searchString.match(/\.(mp3|wav|ogg|m4a|aac)/i)) resolvedType = 'audio';
      }

      return {
        id: p.id,
        url: resolvedUrl,
        description: p.description,
        type: resolvedType,
        title: p.fileName,
      };
    });
  } else {
    activeSlides = [{ id: 'no-photo', type: 'placeholder', description: '' }];
  }

  const hasImagePanelContent = activeSlides.some(
    (s) => s.type !== 'placeholder' && s.type !== 'text' && !!s.url,
  );

  const currentSlide = activeSlides[currentIndex];
  let descriptionContent = currentSlide?.description || liveMarker?.description || '';
  if (
    !descriptionContent ||
    descriptionContent === '<p></p>' ||
    descriptionContent.trim() === ''
  ) {
    descriptionContent = '';
  }
  const activeWebsite = liveMarker?.website;

  const hasDescPanelContent =
    descriptionContent !== '' || (activeWebsite && activeWebsite.trim() !== '');

  const showImagePanel =
    activePanel === 'PHOTOS' && (hasImagePanelContent || !hasDescPanelContent);
  const showDescPanel = activePanel === 'PHOTOS' && hasDescPanelContent;

  const imagePanelHeight = !showDescPanel ? '100%' : '80%';
  const descPanelHeight = !showImagePanel
    ? '100%'
    : isDescriptionExpanded
      ? '80%'
      : '20%';

  const panelWidthNormal = `calc(100% - ${SIDEBAR_WIDTH}px)`;
  const infoLeft = `${SIDEBAR_WIDTH}px`;
  const photosLeft = `${SIDEBAR_WIDTH}px`;
  const docsLeft = `${SIDEBAR_WIDTH}px`;
  const infoWidth = panelWidthNormal;
  const photosWidth = panelWidthNormal;
  const docsWidth = panelWidthNormal;

  const sliderSettings = {
    dots: true,
    infinite: true,
    fade: true,
    cssEase: 'linear',
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    initialSlide: currentIndex,
    beforeChange: (current, next) => setCurrentIndex(next),
  };

  const mediaHash = activeSlides
    .map((s) => String(s.url || 'empty').substring(0, 15))
    .join('|');

  return (
    <Portal>
      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          position: 'fixed',
          top: containerPos.y,
          left: containerPos.x,
          width: `${containerSize}px`,
          height: `${containerSize}px`,
          zIndex: 1610,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          pointerEvents: 'none',
          touchAction: 'none',
          transition:
            isDragging || resizeInfo.current.isResizing ? 'none' : 'all 0.3s ease-in-out',
        }}>
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Box
            className='sidebar-strip'
            title={!liveMarker ? 'VIEW INFO: Select a marker to view details.' : ''}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: SIDEBAR_WIDTH,
              height: '100%',
              backgroundColor: '#333',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 30,
              pointerEvents: 'auto',
            }}>
            <Box
              sx={{
                width: '100%',
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
              }}>
              <IconButton
                className='drag-handle'
                sx={{
                  color: 'white',
                  opacity: 0.5,
                  cursor: 'grab',
                  pointerEvents: 'auto',
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
                title='Drag Panel'>
                <Grid5x5Icon />
              </IconButton>
              <IconButton
                size='small'
                onClick={onDeselect}
                sx={{
                  color: '#fff',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
                title='Close Info'>
                <CloseIcon fontSize='medium' />
              </IconButton>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.currentTarget.blur();
                  setContainerPos({ x: 10, y: 150 });
                  setContainerSize(Math.min(800, window.innerHeight - 160));
                }}
                sx={{
                  color: '#fff',
                  opacity: 1,
                  pointerEvents: 'auto',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
                title='Reset Position & Size'>
                <MenuResetView />
              </IconButton>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.currentTarget.blur();
                  if (!liveMarker) return;
                  navigate('/info-fullscreen', {
                    state: {
                      selectedMarker: liveMarker, // Pass the hydrated DB state
                      activePanel,
                      currentIndex,
                    },
                  });
                }}
                sx={{
                  color: liveMarker ? '#fff' : 'rgba(255, 255, 255, 0.3)',
                  cursor: liveMarker ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: liveMarker ? 'rgba(255,255,255,0.2)' : 'transparent',
                  },
                }}
                title={
                  liveMarker
                    ? 'Open Full Screen Window'
                    : 'Select a marker to use Full Screen'
                }>
                <OpenInFullIcon fontSize='medium' />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                mt: 2,
              }}>
              <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
                <IconButton
                  sx={{ p: 0, width: '100%' }}
                  onClick={() => handleSidebarClick('INFO')}>
                  {activePanel === 'INFO' ? (
                    <MenuGeneralInfoColor sx={{ fontSize: 110 }} />
                  ) : (
                    <MenuGeneralInfoGrey sx={{ fontSize: 110 }} />
                  )}
                </IconButton>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                <IconButton
                  sx={{ p: 0, width: '100%' }}
                  onClick={() => handleSidebarClick('PHOTOS')}>
                  <ClipsIcon
                    sx={{
                      fontSize: 43,
                      filter: activePanel === 'PHOTOS' ? 'none' : 'grayscale(100%)',
                      opacity: activePanel === 'PHOTOS' ? 1 : 0.5,
                    }}
                  />
                </IconButton>
                <Typography
                  sx={{
                    color: activePanel === 'PHOTOS' ? '#fff' : 'rgba(255,255,255,0.8)',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    mt: 0.5,
                    mb: 1,
                    textAlign: 'center',
                    lineHeight: 1.1,
                  }}>
                  Photos
                  <br />
                  Clips
                </Typography>
              </Box>

              <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
                <IconButton
                  sx={{ p: 0, width: '100%' }}
                  onClick={() => handleSidebarClick('DOCS')}>
                  {activePanel === 'DOCS' ? (
                    <MenuDocsColor sx={{ fontSize: 110 }} />
                  ) : (
                    <MenuDocsGrey sx={{ fontSize: 110 }} />
                  )}
                </IconButton>
              </Box>
            </Box>
          </Box>

          {liveMarker && (
            <>
              <Box
                className='info-panel'
                sx={{
                  display: activePanel === 'INFO' ? 'block' : 'none',
                  position: 'absolute',
                  top: 0,
                  left: infoLeft,
                  width: infoWidth,
                  height: '100%',
                  backgroundColor: '#fff',
                  boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
                  borderRight: 'none',
                  pointerEvents: 'auto',
                  overflowY: 'auto',
                  zIndex: 5,
                  transition: 'all 0.3s ease',
                }}>
                <InfoTab data={liveMarker} isEditing={false} />
              </Box>

              <Box
                className='image-panel'
                sx={{
                  display: showImagePanel ? 'block' : 'none',
                  position: 'absolute',
                  top: 0,
                  left: photosLeft,
                  width: photosWidth,
                  height: imagePanelHeight,
                  transition: 'all 0.3s ease',
                  backgroundColor: '#63c2e8',
                  boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
                  borderRight: 'none',
                  pointerEvents: 'auto',
                  overflow: 'hidden',
                  zIndex: 5,
                  '& .slick-slider': { height: '100%', width: '100%' },
                  '& .slick-list': { height: '100%' },
                  '& .slick-track': {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .slick-slide': {
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                  '& .slick-slide > div': { height: '100%', width: '100%' },
                  '& .slick-prev, & .slick-next': {
                    zIndex: 10,
                    width: '32px',
                    height: '32px',
                    display: 'flex !important',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    border: '1px solid #000000',
                    backgroundColor: '#FFFFFF',
                    opacity: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': { opacity: 0.8 },
                    '&:before': {
                      content: '""',
                      borderTop: '2px solid #000',
                      borderLeft: '2px solid #000',
                      width: '10px',
                      height: '10px',
                      position: 'absolute',
                      opacity: 1,
                    },
                  },
                  '& .slick-prev': {
                    left: '10px',
                    '&:before': { transform: 'rotate(-45deg)', marginLeft: '4px' },
                  },
                  '& .slick-next': {
                    right: '10px',
                    '&:before': { transform: 'rotate(135deg)', marginRight: '4px' },
                  },
                  '& .slick-dots': { bottom: '15px', zIndex: 10 },
                  '& .slick-dots li button:before': { display: 'none' },
                  '& .slick-dots li': { width: '14px', height: '14px', margin: '0 4px' },
                  '& .slick-dots li button': {
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    padding: 0,
                    border: '1px solid #000000',
                    backgroundColor: '#FFFFFF',
                    opacity: 0.8,
                  },
                  '& .slick-dots li.slick-active button': {
                    border: '1px solid #FFFFFF',
                    backgroundColor: '#000000',
                    opacity: 1,
                    transform: 'scale(1.2)',
                  },
                }}>
                <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 100 }}>
                  <IconButton
                    onClick={(e) => {
                      e.currentTarget.blur();
                      setIsCoverMode(!isCoverMode);
                    }}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      opacity: 0.3,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', opacity: 1 },
                    }}
                    title={isCoverMode ? 'Show Whole Media' : 'Fill Screen'}>
                    {isCoverMode ? <AspectRatioIcon /> : <CropIcon />}
                  </IconButton>
                </Box>

                {showImagePanel && (
                  <Slider
                    {...sliderSettings}
                    ref={sliderRef}
                    key={`viewinfo-${liveMarker?.id || 'manual'}-${activeSlides.length}-${mediaHash}`}>
                    {activeSlides.map((item, index) => (
                      <Box
                        key={item.id || index}
                        tabIndex={-1}
                        sx={{
                          height: '100%',
                          width: '100%',
                          display: 'flex !important',
                          alignItems: 'center',
                          justifyContent: 'center',
                          outline: 'none',
                        }}>
                        {item.type === 'placeholder' ||
                        item.type === 'text' ||
                        !item.url ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#63c2e8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            <Typography
                              variant='h5'
                              sx={{ color: '#fff', fontStyle: 'italic' }}>
                              No Media Available
                            </Typography>
                          </Box>
                        ) : item.type === 'audio' ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f0f0f0',
                              p: 1,
                              boxSizing: 'border-box',
                            }}>
                            <SoundWaveIcon sx={{ fontSize: 80, mb: 2 }} />
                            <audio
                              src={item.url}
                              controls
                              style={{ width: '80%', height: '40px' }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>
                        ) : (
                          <MediaSlide
                            item={item}
                            isActive={index === currentIndex}
                            objectFit={isCoverMode ? 'cover' : 'contain'}
                            shouldAutoPlay={false}
                          />
                        )}
                      </Box>
                    ))}
                  </Slider>
                )}
              </Box>

              <Box
                className='description-panel'
                sx={{
                  display: showDescPanel ? 'block' : 'none',
                  position: 'absolute',
                  bottom: 0,
                  left: photosLeft,
                  width: photosWidth,
                  height: descPanelHeight,
                  transition: 'all 0.3s ease',
                  zIndex: isDescriptionExpanded ? 10 : 5,
                  backgroundColor: '#fff',
                  borderTop: '1px solid #e0e0e0',
                  borderRight: 'none',
                  p: 2,
                  overflowY: isDescriptionExpanded ? 'auto' : 'hidden',
                  pointerEvents: 'auto',
                  boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
                }}>
                {activeWebsite && activeWebsite.trim() !== '' && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                    <IconButton
                      onClick={() => handleOpenWeb(activeWebsite)}
                      title='Visit Website'
                      sx={{
                        width: '48px !important',
                        height: '48px !important',
                        minWidth: '48px !important',
                        minHeight: '48px !important',
                        padding: '0 !important',
                        border: '2px solid #FF9900',
                        borderRadius: '6px',
                        color: '#00008B',
                        bgcolor: '#fff',
                        '&:hover': { bgcolor: '#f0f0f0' },
                      }}>
                      <LanguageIcon sx={{ fontSize: '32px !important' }} />
                    </IconButton>
                  </Box>
                )}

                {descriptionContent && (
                  <Box
                    sx={{
                      mt: activeWebsite && activeWebsite.trim() !== '' ? 3 : 0,
                      typography: 'body1',
                      color: 'text.secondary',
                      lineHeight: 1.2,
                      minHeight: '20px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      '& p': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.2 },
                      '& p:last-of-type': { marginBottom: 0 },
                      '& img': { maxWidth: '100%', height: 'auto' },
                      '& ul, & ol': {
                        paddingLeft: '1.5rem',
                        marginTop: 0,
                        marginBottom: '3pt',
                      },
                      '& li': { marginTop: 0, marginBottom: '3pt', lineHeight: 1.1 },
                      '& a': { color: '#1976d2', textDecoration: 'none' },
                    }}
                    dangerouslySetInnerHTML={{ __html: descriptionContent }}
                  />
                )}

                {showDescPanel && showImagePanel && (
                  <Button
                    className='see-more-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDescriptionExpanded(!isDescriptionExpanded);
                    }}
                    variant='contained'
                    size='small'
                    sx={{
                      position: 'absolute',
                      bottom: 10,
                      right: 45,
                      zIndex: 10,
                      textTransform: 'none',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                    }}>
                    {isDescriptionExpanded ? 'See less ...' : 'See more ...'}
                  </Button>
                )}
              </Box>

              <Box
                className='docs-panel'
                sx={{
                  display: activePanel === 'DOCS' ? 'block' : 'none',
                  position: 'absolute',
                  top: 0,
                  left: docsLeft,
                  width: docsWidth,
                  height: '100%',
                  backgroundColor: '#fff',
                  boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
                  pointerEvents: 'auto',
                  overflowY: 'auto',
                  zIndex: 5,
                  transition: 'all 0.3s ease',
                }}>
                <DocsView docs={liveMarker.documents || []} />
              </Box>
            </>
          )}

          {liveMarker && (
            <Box
              className='resize-handle'
              onPointerDown={handleResizePointerDown}
              onPointerMove={handleResizePointerMove}
              onPointerUp={handleResizePointerUp}
              onPointerCancel={handleResizePointerUp}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '30px',
                height: '30px',
                cursor: 'nwse-resize',
                zIndex: 100,
                pointerEvents: 'auto',
                touchAction: 'none',
                background: showImagePanel
                  ? 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.8) 50%)'
                  : 'linear-gradient(135deg, transparent 50%, rgba(128,128,128,0.8) 50%)',
                '&:hover': {
                  background: showImagePanel
                    ? 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,1) 50%)'
                    : 'linear-gradient(135deg, transparent 50%, rgba(128,128,128,1) 50%)',
                },
              }}
              title='Drag to resize'
            />
          )}
        </Box>
      </Box>
    </Portal>
  );
}
